import { Injectable } from '@angular/core';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { cloneDeep } from 'lodash-es';
import { aircraft as aircraftData} from './data';

@Injectable({ providedIn: 'root' })
export class AircraftMockApi {
    private _aircraft: any[] = aircraftData;

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Aircraft - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/apps/aircraft/all', 300)
        .reply(({request}) => {
            // Get available queries
            const search = request.params.get('search');
            const sort = request.params.get('sort') || 'model';
            const order = request.params.get('order') || 'asc';
            const page = parseInt(request.params.get('page') ?? '1', 10);
            const size = parseInt(request.params.get('size') ?? '10', 10);

            // Clone the aircraft
            let aircraft = cloneDeep(this._aircraft);

            // Sort the aircraft by the name field by default
            if(sort === 'model' || sort === 'status' || sort === 'home_airport') {
                aircraft.sort((a, b) => {
                    const fieldA = a[sort].toString().toUpperCase();
                    const fieldB = b[sort].toString().toUpperCase();
                    return order === 'asc'
                        ? fieldA.localeCompare(fieldB)
                        : fieldB.localeCompare(fieldA);
                })
            }else{
                aircraft.sort((a, b) => 
                    order === 'asc' ? a[sort] - b[sort] : b[sort] - a[sort]
                );
            }
            
            // If search exists...
            if (search) {
                // Filter the products
                aircraft = aircraft.filter(
                    (aircraft) =>
                        aircraft.model &&
                        aircraft.model
                            .toLowerCase()
                            .includes(search.toLowerCase())
                );
            }

            // Paginate - Start
            const aircraftLength = aircraft.length;

            // Calculate pagination details
            const begin = page * size;
            const end = Math.min(size * (page + 1), aircraftLength);
            const lastPage = Math.max(Math.ceil(aircraftLength / size), 1);

            // Prepare the pagination object
            let pagination = {};

            // If the requested page number is bigger than
            // the last possible page number, return null for
            // products but also send the last possible page so
            // the app can navigate to there
            if (page > lastPage) {
                aircraft = null;
                pagination = {
                    lastPage,
                };
            } else {
                // Paginate the results by size
                aircraft = aircraft.slice(begin, end);
                // Prepare the pagination mock-api
                pagination = {
                    length: aircraftLength,
                    size: size,
                    page: page,
                    lastPage: lastPage,
                    startIndex: begin,
                    endIndex: end - 1,
                };
            }

            // Return the response
            return [
                200, 
                {
                    aircraft,
                    pagination
                }
            ];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ Contacts Search - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/aircraft/search')
            .reply(({ request }) => {
                // Get the search query
                const query = request.params.get('query');

                // Clone the contacts
                let aircraft = cloneDeep(this._aircraft);

                // If the query exists...
                if (query) {
                    // Filter the contacts
                    aircraft = aircraft.filter(
                        (aircraft) =>
                            aircraft.model &&
                            aircraft.model
                                .toLowerCase()
                                .includes(query.toLowerCase())
                    );
                }

                // Sort the contacts by the name field by default
                aircraft.sort((a, b) => a.model.localeCompare(b.model));

                // Return the response
                return [200, aircraft];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Contact - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/aircraft/plane')
            .reply(({ request }) => {
                // Get the id from the params
                const id = request.params.get('id');

                // Clone the contacts
                const aircraft = cloneDeep(this._aircraft);

                // Find the contact
                const plane = aircraft.find((item) => item.id === id);

                // Return the response
                return [200, plane];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Contact - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/aircraft/plane')
            .reply(({ request }) => {
                // Get the id
                const id = request.params.get('id');

                // Find the contact and delete it
                this._aircraft.forEach((item, index) => {
                    if (item.id === id) {
                        this._aircraft.splice(index, 1);
                    }
                });

                // Return the response
                return [200, true];
            });
    }
}
